import type {Values} from '@wix/devzai-utils-common';
import type {BymoPageContextProps} from '../../doppe-sdk/bymo-page/bymo-page-context-props';
import {
    doppeHtmlSiteResolveProductPageUrl,
    doppeHtmlSiteResolveServicePageUrl,
    doppeHtmlSiteResolveBlogPostPageUrl,
    doppeHtmlSiteResolveEventPageUrl,
    doppeHtmlSiteResolveAllProductsPageUrl,
    doppeHtmlSiteResolveBookOnlinePageUrl,
    doppeHtmlSiteResolveAllPostsPageUrl,
    doppeHtmlSiteResolveEventsPageURL
} from '../types/doppe-html-site';
import type {DoppeViewerLang} from '../doppe-viewer/doppe-viewer-lang';

/**
 * Vertical entity types for navigation
 */
export const DoppeVerticalEntityType = {
    Product: 'Product',
    Service: 'Service',
    BlogPost: 'BlogPost',
    Event: 'Event',
} as const;

export type DoppeVerticalEntityType = Values<typeof DoppeVerticalEntityType>;

/**
 * Entity info required for navigation - contains slug and page URL for fallback
 */
export interface VerticalEntityInfo {
    /** The slug used for Hopp internal routing */
    slug?: string | null;
    /** The full URL to the entity page on Wix site (used when useHoppVerticalItemPages is false) */
    pageUrl?: string | null;
}

/**
 * Options for vertical entity navigation
 */
export interface VerticalEntityNavigationOptions {
    /** Context props containing the useHoppVerticalItemPages flag and htmlSiteInfo */
    bymoPageContextProps: BymoPageContextProps;
    /** The viewer language */
    viewerLang: DoppeViewerLang;
    /** Whether to show mobile view (for htmlSiteInfo URLs) */
    showMobileView: boolean;
}

/**
 * Result of determining navigation target for a vertical entity
 */
export interface VerticalEntityNavigationResult {
    /** Whether to use Hopp's internal routing */
    useInternalRouting: boolean;
    /** The slug to use for internal routing (only if useInternalRouting is true) */
    slug?: string;
    /** The external URL to open (only if useInternalRouting is false) */
    externalUrl?: string | null;
}

/**
 * Determines the navigation target for a product entity.
 * 
 * When useHoppVerticalProductPages is true: Returns internal routing with product slug
 * When useHoppVerticalProductPages is false: Returns external URL from htmlSiteInfo
 */
export function viewerEntityNavigationResolveProductTarget(
    entityInfo: VerticalEntityInfo & { productPageUrl?: string | null },
    options: VerticalEntityNavigationOptions
): VerticalEntityNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalProductPages = bymoPageContextProps.useHoppVerticalProductPages ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalProductPages && entityInfo.slug) {
        return {
            useInternalRouting: true,
            slug: entityInfo.slug
        };
    }

    // Fall back to external URL
    const productPageUrl = entityInfo.productPageUrl ?? entityInfo.pageUrl;
    if (htmlSiteInfo && productPageUrl) {
        const externalUrl = doppeHtmlSiteResolveProductPageUrl({
            doppeHtmlSiteInfo: htmlSiteInfo,
            productPageUrl: productPageUrl,
            viewerLang: viewerLang,
            showMobileView: showMobileView
        });

        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for a service entity.
 * 
 * When useHoppVerticalServicePages is true: Returns internal routing with service slug
 * When useHoppVerticalServicePages is false: Returns external URL from htmlSiteInfo
 */
export function viewerEntityNavigationResolveServiceTarget(
    entityInfo: VerticalEntityInfo & { servicePageUrl?: string | null },
    options: VerticalEntityNavigationOptions
): VerticalEntityNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalServicePages = bymoPageContextProps.useHoppVerticalServicePages ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalServicePages && entityInfo.slug) {
        return {
            useInternalRouting: true,
            slug: entityInfo.slug
        };
    }

    // Fall back to external URL
    const servicePageUrl = entityInfo.servicePageUrl ?? entityInfo.pageUrl;
    if (htmlSiteInfo && servicePageUrl) {
        const externalUrl = doppeHtmlSiteResolveServicePageUrl({
            doppeHtmlSiteInfo: htmlSiteInfo,
            pageURL: servicePageUrl,
            viewerLang: viewerLang,
            showMobileView: showMobileView
        });

        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for a blog post entity.
 * 
 * When useHoppVerticalBlogPostPages is true: Returns internal routing with post slug
 * When useHoppVerticalBlogPostPages is false: Returns external URL from htmlSiteInfo
 */
export function viewerEntityNavigationResolveBlogPostTarget(
    entityInfo: VerticalEntityInfo,
    options: VerticalEntityNavigationOptions
): VerticalEntityNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalBlogPostPages = bymoPageContextProps.useHoppVerticalBlogPostPages ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalBlogPostPages && entityInfo.slug) {
        return {
            useInternalRouting: true,
            slug: entityInfo.slug
        };
    }

    // Fall back to external URL using slug
    if (htmlSiteInfo && entityInfo.slug) {
        const externalUrl = doppeHtmlSiteResolveBlogPostPageUrl({
            doppeHtmlSiteInfo: htmlSiteInfo,
            slug: entityInfo.slug,
            viewerLang: viewerLang,
            showMobileView: showMobileView
        });

        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for an event entity.
 * 
 * When useHoppVerticalEventPages is true: Returns internal routing with event slug
 * When useHoppVerticalEventPages is false: Returns external URL from htmlSiteInfo
 */
export function viewerEntityNavigationResolveEventTarget(
    entityInfo: VerticalEntityInfo,
    options: VerticalEntityNavigationOptions
): VerticalEntityNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalEventPages = bymoPageContextProps.useHoppVerticalEventPages ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalEventPages && entityInfo.slug) {
        return {
            useInternalRouting: true,
            slug: entityInfo.slug
        };
    }

    // Fall back to external URL using slug
    if (htmlSiteInfo && entityInfo.slug) {
        const externalUrl = doppeHtmlSiteResolveEventPageUrl({
            doppeHtmlSiteInfo: htmlSiteInfo,
            slug: entityInfo.slug,
            viewerLang: viewerLang,
            showMobileView: showMobileView
        });

        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Opens an external URL based on the viewer mode.
 * 
 * In live preview mode: Opens the URL in a modal using the provided callback
 * In live viewer mode: Navigates to the URL in the same tab
 */
export function viewerEntityNavigationOpenExternalUrl(
    url: string,
    options: {
        isLivePreviewMode: boolean;
        openInModal: (url: string) => void;
    }
): void {
    if (options.isLivePreviewMode) {
        options.openInModal(url);
    } else {
        window.location.href = url;
    }
}

// ============================================================================
// List Page Navigation
// ============================================================================

/**
 * Options for vertical list page navigation
 */
export interface VerticalListNavigationOptions {
    /** Context props containing the useHoppVerticalListPage flags and htmlSiteInfo */
    bymoPageContextProps: BymoPageContextProps;
    /** The viewer language */
    viewerLang: DoppeViewerLang;
    /** Whether to show mobile view (for htmlSiteInfo URLs) */
    showMobileView: boolean;
}

/**
 * Result of determining navigation target for a vertical list page
 */
export interface VerticalListNavigationResult {
    /** Whether to use Hopp's internal routing */
    useInternalRouting: boolean;
    /** The external URL to open (only if useInternalRouting is false) */
    externalUrl?: string | null;
}

/**
 * Determines the navigation target for the Products LIST page.
 * 
 * When useHoppVerticalProductListPage is true: Returns internal routing to products list
 * When useHoppVerticalProductListPage is false: Returns external URL to HTML site Store page
 */
export function viewerListNavigationResolveProductsListTarget(
    options: VerticalListNavigationOptions
): VerticalListNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalProductListPage = bymoPageContextProps.useHoppVerticalProductListPage ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalProductListPage) {
        return {
            useInternalRouting: true
        };
    }

    // Fall back to external URL
    if (htmlSiteInfo) {
        const externalUrl = doppeHtmlSiteResolveAllProductsPageUrl(htmlSiteInfo, viewerLang, showMobileView);
        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for the Services LIST page.
 * 
 * When useHoppVerticalServiceListPage is true: Returns internal routing to services list
 * When useHoppVerticalServiceListPage is false: Returns external URL to HTML site Booking Services page
 */
export function viewerListNavigationResolveServicesListTarget(
    options: VerticalListNavigationOptions
): VerticalListNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalServiceListPage = bymoPageContextProps.useHoppVerticalServiceListPage ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalServiceListPage) {
        return {
            useInternalRouting: true
        };
    }

    // Fall back to external URL
    if (htmlSiteInfo) {
        const externalUrl = doppeHtmlSiteResolveBookOnlinePageUrl(htmlSiteInfo, viewerLang, showMobileView);
        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for the Blog Posts LIST page.
 * 
 * When useHoppVerticalBlogPostListPage is true: Returns internal routing to blog posts list
 * When useHoppVerticalBlogPostListPage is false: Returns external URL to HTML site Blog page
 */
export function viewerListNavigationResolveBlogPostsListTarget(
    options: VerticalListNavigationOptions
): VerticalListNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalBlogPostListPage = bymoPageContextProps.useHoppVerticalBlogPostListPage ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalBlogPostListPage) {
        return {
            useInternalRouting: true
        };
    }

    // Fall back to external URL
    if (htmlSiteInfo) {
        const externalUrl = doppeHtmlSiteResolveAllPostsPageUrl(htmlSiteInfo, viewerLang, showMobileView);
        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for the Events LIST page.
 * 
 * When useHoppVerticalEventListPage is true: Returns internal routing to events list
 * When useHoppVerticalEventListPage is false: Returns external URL to HTML site Events page
 */
export function viewerListNavigationResolveEventsListTarget(
    options: VerticalListNavigationOptions
): VerticalListNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalEventListPage = bymoPageContextProps.useHoppVerticalEventListPage ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalEventListPage) {
        return {
            useInternalRouting: true
        };
    }

    // Fall back to external URL
    if (htmlSiteInfo) {
        const externalUrl = doppeHtmlSiteResolveEventsPageURL(htmlSiteInfo, viewerLang, showMobileView);
        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

